import React from 'react'
import { BaseView, BaseText } from '@/components/base'
import { ConfirmAvatar } from '@/app/send-request/confirm/components/ConfirmAvatar'

interface UserProfileInfoProps {
  avatarUrl?: string
  firstName?: string
  lastName?: string
  username?: string
  phoneNumber?: string
  avatarBg?: string[]
  isBlocked?: boolean
  isFavorite?: boolean
  isVerified?: boolean
  isLoading?: boolean
}

export const UserProfileInfo: React.FC<UserProfileInfoProps> = ({
  avatarUrl,
  firstName,
  lastName,
  username,
  phoneNumber,
  avatarBg,
  isBlocked = false,
  isFavorite = false,
  isLoading = false,
}) => {
  const displayName =
    `${firstName || ''} ${lastName || ''}`.trim() || 'Unknown User'
  const subtitle = username ? `@${username}` : phoneNumber

  if (isLoading) {
    return (
      <BaseView className="items-center" style={{ marginTop: '14px' }}>
        {/* Avatar skeleton - 110px to match ConfirmAvatar size */}
        <BaseView
          className="rounded-full bg-gray-200 dark:bg-gray-700 animate-pulse"
          style={{
            width: '110px',
            height: '110px',
            marginBottom: '16px', // gap={16} from real content
          }}
        />

        {/* Name and subtitle container with gap={6} */}
        <BaseView className="items-center" style={{ gap: '25px' }}>
          {/* Name skeleton - fontSize={20} fontFamily="semibold" */}
          <BaseView
            className="bg-gray-200 dark:bg-gray-700 rounded animate-pulse"
            style={{
              height: '24px', // ~20px font + line height
              width: '160px', // Wider for full names
            }}
          />

          {/* Subtitle skeleton - fontFamily="medium" */}
          <BaseView
            className="bg-gray-200 dark:bg-gray-700 rounded animate-pulse"
            style={{
              height: '20px', // Default font size + line height
              width: '120px',
            }}
          />
        </BaseView>
      </BaseView>
    )
  }

  return (
    <BaseView className="items-center">
      {/* Avatar Block - как в send-request/confirm */}
      <BaseView
        data-component="user-profile-avatar"
        align="center"
        gap={16}
        style={{ marginTop: '14px' }}
      >
        <ConfirmAvatar
          size={110}
          uri={avatarUrl}
          iconSize={24}
          name={displayName}
          gradientColors={
            avatarBg ? (avatarBg as [string, string, ...string[]]) : undefined
          }
          userIsFavorite={isFavorite}
        />
        <BaseView data-component="user-profile-name" align="center" gap={6}>
          <BaseText fontSize={20} fontFamily="semibold" color="label.default">
            {displayName}
          </BaseText>
          <BaseText fontFamily="medium" color="label.clickable">
            {subtitle || ''}
          </BaseText>
        </BaseView>
      </BaseView>

      {/* Blocked indicator */}
      {isBlocked && (
        <BaseView
          className="px-4 py-2 rounded-full mt-4"
          style={{
            backgroundColor: '#EF4444',
            opacity: 0.9,
          }}
        >
          <BaseText fontSize={14} fontFamily="medium" className="text-white">
            Blocked
          </BaseText>
        </BaseView>
      )}
    </BaseView>
  )
}
